import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Box, Button, Chip, FormControl, MenuItem, OutlinedInput, Select, Stack, TextField } from "@mui/material";

import Header from "../Header";

import { api } from "../../utils";
import { IError } from "../../models/ReportPageModels";

interface DeduplicationCompaniesResponseModel {
    data: DeduplicationCompaniesData;
    error: IError;
}
interface DeduplicationCompaniesData {
    companies: string[];
}

interface CompanyInformationResponseModel {
    data: CompanyInformationData;
    error: IError;
}
interface CompanyInformationData {
    deduplicationDays: number;
    booths: BoothClientModel[];
}
interface BoothClientModel {
    boothId: string;
    name: string;
}

interface LoadDeduplicationRequestModel {
    month: Dayjs;
    company: string;
    deduplicationDays: number;
    boothFilter: string[];
}


function Deduplication() {
    const { enqueueSnackbar } = useSnackbar();

    var monthCopy = useRef<Dayjs>(dayjs());
    const [month, setMonth] = useState<Dayjs>(dayjs());

    const [companies, setCompanies] = useState<string[]>([]);

    var selectedCompanyCopy = useRef<string>("");
    const [selectedCompany, setSelectedCompany] = useState<string>("");

    const [enabled, setEnabled] = useState(false);

    const [allBoothIds, setAllBoothIds] = useState<BoothClientModel[]>([]);

    const [selectedBoothIds, setSelectedBoothIds] = useState<string[]>([]);

    const [deduplicationDay, setDeduplicationDay] = useState<number>(0);

    useEffect(() => {
        GetCompanies();
    }, [])

    const GetCompanies = async () => {
        try {
            var resp: DeduplicationCompaniesResponseModel = await api.get("api/Deduplication/GetCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setCompanies(resp.data.companies);
            }
            else {
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function SelectCompany(event: any) {
        var value = event.target.value;
        setSelectedCompany(value);
        selectedCompanyCopy.current = value;
        GetCompanyInformation();
    }

    const GetCompanyInformation = async () => {
        setEnabled(false);
        setSelectedBoothIds([]);

        try {
            var resp: CompanyInformationResponseModel = await api.get(`api/Deduplication/GetInformation?companyName=${selectedCompanyCopy.current}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (resp.error === null) {
                setEnabled(true)
                setAllBoothIds(resp.data.booths);
                setDeduplicationDay(resp.data.deduplicationDays)
            }
            else {
                setAllBoothIds([]);
                setDeduplicationDay(0);
                enqueueSnackbar(resp.error.description, { variant: "error" });
            }
        } catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const LoadDeduplication = async () => {
        const reqBody: LoadDeduplicationRequestModel = {
            month: monthCopy.current,
            company: selectedCompanyCopy.current,
            deduplicationDays: deduplicationDay,
            boothFilter: selectedBoothIds
        }

        const response = await api.post("api/Deduplication/Deduplication", {
            json: reqBody,
            headers: {
                "Authorization": localStorage.getItem("token")!
            }
        }).blob();

        const url = window.URL.createObjectURL(
            new Blob([response]),
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `${selectedCompanyCopy.current} Report.csv`,
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }

    return (
        <>
            <Header headerType={"BILLING"} displayLogout={false} />
            <Stack direction='row'>
                <Stack
                    direction="column"
                    sx={{ width: "320px" }}
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1.5}>
                    <span>Select month</span>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            format="MM/YYYY"
                            views={["year", "month"]}
                            openTo={"month"}
                            value={month}
                            onChange={(param: any) => {
                                setMonth(param);
                                monthCopy.current = dayjs(param);
                            }}
                        />
                    </LocalizationProvider>

                    <p>Client</p>
                    <FormControl sx={{ width: "250px" }}>
                        <Select
                            value={selectedCompany}
                            onChange={SelectCompany}
                            size="small"
                            defaultValue=''>
                            {companies.map((item, index) => (
                                <MenuItem value={item} key={index}>{item}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <p>BoothID</p>
                    <FormControl sx={{ minWidth: '250px', maxWidth: '250px' }}>
                        <Select
                            disabled={!enabled}
                            multiple
                            value={selectedBoothIds ?? []}
                            onChange={(e: any) => setSelectedBoothIds(e.target.value)}
                            size="small"
                            input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} />
                                    ))}
                                </Box>
                            )}>
                            {allBoothIds.map((item, pIndex) => (
                                <MenuItem value={item.boothId} key={pIndex + 1}>
                                    <span>{item.boothId} - {item.name}</span>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <p>Deduplication days</p>
                    <TextField
                        disabled={!enabled}
                        value={deduplicationDay}
                        onChange={(event: any) => setDeduplicationDay(event.target.value)}
                        sx={{ minWidth: '250px' }}
                        size="small" />

                    <Button
                        variant="outlined"
                        onClick={LoadDeduplication}
                        sx={{ left: '0px' }}>
                        Download Deduplication report
                    </Button>
                </Stack>
            </Stack>
        </>
    )
}

export default Deduplication;